/**
 * Experience Type Labels Utility
 *
 * Centralized configuration for consistent experience type labels across all screens.
 * Ensures unanimous labeling: Activity, Nexgo Space, Package, Riders Corner
 */

export type ExperienceType =
  | 'activities'
  | 'package'
  | 'riders-corner'
  | 'signature'
  | 'nexgo-journeys'
  | 'stays';

/**
 * Standardized labels for experience types
 * Used across all screens: experiences list, landing page, chat screen, etc.
 */
export const EXPERIENCE_TYPE_LABELS: Record<ExperienceType, string> = {
  activities: 'Activity',
  package: 'Package',
  'riders-corner': 'Riders Corner',
  signature: 'Signature Journeys',
  'nexgo-journeys': 'Nexgo Journeys',
  stays: 'Nexgo Space',
};

/**
 * Get the standardized label for an experience type
 * @param type - The experience type (e.g., 'activities', 'stays', 'package')
 * @returns The standardized label string
 */
export function getExperienceTypeLabel(type: ExperienceType | string): string {
  // Normalize the type to handle variations
  const normalizedType = type.toLowerCase().replace(/[_-]/g, '-');

  // Map common variations to standard types
  const typeMap: Record<string, ExperienceType> = {
    activity: 'activities',
    activities: 'activities',
    guidedactivity: 'activities',
    'guided-activities': 'activities',
    package: 'package',
    packages: 'package',
    'curated-packages': 'package',
    'riders-corner': 'riders-corner',
    riderscorner: 'riders-corner',
    riders_corner: 'riders-corner',
    signature: 'signature',
    'signature-experience': 'signature',
    'signature-journeys': 'nexgo-journeys',
    'nexgo-journeys': 'nexgo-journeys',
    stays: 'stays',
    stay: 'stays',
    'stay-experience': 'stays',
    stay_experience: 'stays',
    'boutique-stays': 'stays',
  };

  const mappedType =
    typeMap[normalizedType] || (normalizedType as ExperienceType);

  return EXPERIENCE_TYPE_LABELS[mappedType as ExperienceType] || 'Experience';
}

/**
 * Section titles for experience lists (can be plural/more descriptive)
 * Used in section headers, filters, etc.
 */
export const EXPERIENCE_SECTION_TITLES: Record<ExperienceType, string> = {
  activities: 'Guided Activities',
  package: 'Nexgo Journeys',
  'riders-corner': 'Riders Corner',
  signature: 'Nexgo Journeys',
  'nexgo-journeys': 'Nexgo Journeys',
  stays: 'Nexgo Spaces',
};

/**
 * Get section title for experience type (plural/descriptive version)
 * @param type - The experience type
 * @returns The section title string
 */
export function getExperienceSectionTitle(
  type: ExperienceType | string
): string {
  const normalizedType = type.toLowerCase().replace(/[_-]/g, '-');

  const typeMap: Record<string, ExperienceType> = {
    activity: 'activities',
    activities: 'activities',
    guidedactivity: 'activities',
    'guided-activities': 'activities',
    package: 'package',
    packages: 'package',
    'curated-packages': 'package',
    'riders-corner': 'riders-corner',
    riderscorner: 'riders-corner',
    riders_corner: 'riders-corner',
    signature: 'signature',
    'signature-experience': 'signature',
    'signature-journeys': 'nexgo-journeys',
    'nexgo-journeys': 'nexgo-journeys',
    stays: 'stays',
    stay: 'stays',
    'stay-experience': 'stays',
    stay_experience: 'stays',
    'boutique-stays': 'stays',
  };

  const mappedType =
    typeMap[normalizedType] || (normalizedType as ExperienceType);

  return (
    EXPERIENCE_SECTION_TITLES[mappedType as ExperienceType] || 'Experiences'
  );
}

/**
 * Section descriptions for experience lists
 * Used in section headers to provide context about each experience type
 */
export const EXPERIENCE_SECTION_DESCRIPTIONS: Record<ExperienceType, string> = {
  activities: 'Handpicked activities at every destination',
  package: 'Ready-made travel plans for every type of traveler',
  'riders-corner': 'Exclusive motorcycle tours and adventures',
  signature: 'Nexgo crafted travel stories that go beyond the ordinary!',
  'nexgo-journeys': 'Thoughtfully curated journeys for meaningful and refined exploration',
  stays: 'Unique stays handpicked for comfort and character',
};

/**
 * Get section description for experience type
 * @param type - The experience type
 * @returns The section description string
 */
export function getExperienceSectionDescription(
  type: ExperienceType | string
): string {
  const normalizedType = type.toLowerCase().replace(/[_-]/g, '-');

  const typeMap: Record<string, ExperienceType> = {
    activity: 'activities',
    activities: 'activities',
    guidedactivity: 'activities',
    'guided-activities': 'activities',
    package: 'package',
    packages: 'package',
    'curated-packages': 'package',
    'riders-corner': 'riders-corner',
    riderscorner: 'riders-corner',
    riders_corner: 'riders-corner',
    signature: 'signature',
    'signature-experience': 'signature',
    'signature-journeys': 'nexgo-journeys',
    'nexgo-journeys': 'nexgo-journeys',
    stays: 'stays',
    stay: 'stays',
    'stay-experience': 'stays',
    stay_experience: 'stays',
    'boutique-stays': 'stays',
  };

  const mappedType =
    typeMap[normalizedType] || (normalizedType as ExperienceType);

  return EXPERIENCE_SECTION_DESCRIPTIONS[mappedType as ExperienceType] || '';
}
